import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Chip,
  Button,
  InputAdornment,
  alpha,
  Card,
  CardContent,
  List,
  ListItem,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  DescriptionOutlined as FormIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  FolderOpen as FolderOpenIcon,
  Dashboard as DashboardIcon,
  Publish as PublishIcon,
  DraftsOutlined as DraftIcon,
  Category as CategoryIcon,
  AccountCircleOutlined as UserIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import FormEdit from "./FormEdit";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import CustomInput from "../../CustomForm/CustomInput";

const CarePlans = ({ forms, handleCreate, token }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmModal, setConfirmModal] = useState({ show: false, formId: null });

  const filteredForms = React.useMemo(() => {
    if (!searchTerm) return forms || [];
    return (forms || []).filter((form) =>
      form.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, forms]);

  const selectedForm = React.useMemo(() => {
    if (!selectedFormId || !forms) return null;
    return forms.find((f) => f.id === selectedFormId);
  }, [selectedFormId, forms]);

  const handlePublishClick = (e, id) => {
    e.stopPropagation();
    setConfirmModal({ show: true, formId: id });
  };

  const handleConfirmPublish = async () => {
    try {
      enqueueSnackbar("Care plan published successfully!", {
        variant: "success",
      });
      setConfirmModal({ show: false, formId: null });
    } catch (error) {
      enqueueSnackbar("Failed to publish care plan.", { variant: "error" });
    }
  };

  const totalForms = forms?.length || 0;
  const publishedForms = forms?.filter((f) => f.is_active)?.length || 0;
  const draftForms = forms?.filter((f) => !f.is_active)?.length || 0;

  return (
    <Box sx={{ display: "flex", gap: 2, width: "100%", flexWrap: "wrap" }}>
      <Box sx={{ flex: "0 0 300px", minWidth: "260px" }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #EAECF0",
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#FFFFFF",
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: "1px solid #EAECF0" }}>
            <CustomInput
              placeholder="Search care plans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94A3B8", fontSize: 16 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ maxHeight: "560px", overflowY: "auto" }}>
            {filteredForms.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <FormIcon sx={{ fontSize: 32, color: "#D0D5DD", mb: 1 }} />
                <Typography sx={{ fontSize: "0.75rem", color: "#667085" }}>
                  {searchTerm
                    ? `No results for "${searchTerm}"`
                    : "No care plans available"}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 1 }}>
                {filteredForms.map((form) => (
                  <ListItem
                    key={form.id}
                    onClick={() => setSelectedFormId(form.id)}
                    sx={{
                      borderRadius: "8px",
                      mb: 0.5,
                      cursor: "pointer",
                      alignItems: "flex-start",
                      px: 1.25,
                      py: 1,
                      bgcolor:
                        selectedFormId === form.id
                          ? alpha("#2469F0", 0.06)
                          : "transparent",
                      border:
                        selectedFormId === form.id
                          ? `1px solid ${alpha("#2469F0", 0.2)}`
                          : "1px solid transparent",
                      "&:hover": {
                        bgcolor:
                          selectedFormId === form.id
                            ? alpha("#2469F0", 0.1)
                            : "#F8FAFC",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "8px",
                        bgcolor: alpha("#2469F0", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.25,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      <FolderOpenIcon
                        sx={{ color: "#2469F0", fontSize: 15 }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#101828",
                          mb: 0.4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {form.title}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Chip
                          label={form.is_active ? "Published" : "Draft"}
                          size="small"
                          sx={{
                            height: 17,
                            fontSize: "0.55rem",
                            fontWeight: 600,
                            bgcolor: form.is_active ? "#ECFDF3" : "#F2F4F7",
                            color: form.is_active ? "#027A48" : "#344054",
                            border: `1px solid ${
                              form.is_active ? "#A6F4C5" : "#EAECF0"
                            }`,
                            borderRadius: "4px",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#98A2B3",
                            fontWeight: 500,
                          }}
                        >
                          {form.created_at
                            ? new Date(form.created_at).toLocaleDateString(
                                "en-GB",
                                { day: "numeric", month: "short" }
                              )
                            : "N/A"}
                        </Typography>
                      </Stack>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Box
            sx={{
              p: 1.25,
              borderTop: "1px solid #EAECF0",
              bgcolor: "#F8FAFC",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={handleCreate}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 34,
                bgcolor: "#2469F0",
                boxShadow: "none",
                "&:hover": { bgcolor: "#1A5BD4" },
              }}
            >
              New Template
            </Button>
          </Box>
        </Paper>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 1,
              borderRadius: "10px",
              border: "1px solid #EAECF0",
              bgcolor: "#F8FAFC",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <DashboardIcon sx={{ color: "#2469F0", fontSize: 15 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.5rem",
                  color: "#667085",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Total
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: "#101828",
                }}
              >
                {totalForms}
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 1,
              borderRadius: "10px",
              border: "1px solid #EAECF0",
              bgcolor: "#F0FDF4",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <PublishIcon sx={{ color: "#16A34A", fontSize: 15 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.5rem",
                  color: "#667085",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Live
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: "#101828",
                }}
              >
                {publishedForms}
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 1,
              borderRadius: "10px",
              border: "1px solid #EAECF0",
              bgcolor: "#FFFBEB",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <DraftIcon sx={{ color: "#B45309", fontSize: 15 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.5rem",
                  color: "#667085",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Draft
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: "#101828",
                }}
              >
                {draftForms}
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minWidth: "300px" }}>
        {selectedForm ? (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #EAECF0",
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: "#FFFFFF",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #EAECF0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    bgcolor: alpha("#2469F0", 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FolderOpenIcon sx={{ color: "#2469F0", fontSize: 18 }} />
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: "#101828",
                      }}
                    >
                      {selectedForm.title}
                    </Typography>
                    <Chip
                      label={selectedForm.is_active ? "Published" : "Draft"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        bgcolor: selectedForm.is_active
                          ? "#ECFDF3"
                          : "#F2F4F7",
                        color: selectedForm.is_active ? "#027A48" : "#344054",
                        border: `1px solid ${
                          selectedForm.is_active ? "#A6F4C5" : "#EAECF0"
                        }`,
                        borderRadius: "6px",
                      }}
                    />
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ mt: 0.4 }}
                    alignItems="center"
                  >
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <UserIcon sx={{ fontSize: 12, color: "#98A2B3" }} />
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: "#667085",
                          fontWeight: 500,
                        }}
                      >
                        {selectedForm.created_by_name || "System Admin"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <CalendarIcon sx={{ fontSize: 12, color: "#98A2B3" }} />
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: "#667085",
                          fontWeight: 500,
                        }}
                      >
                        {selectedForm.created_at
                          ? new Date(selectedForm.created_at).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : "N/A"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <FormIcon sx={{ fontSize: 12, color: "#98A2B3" }} />
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: "#667085",
                          fontWeight: 500,
                        }}
                      >
                        {selectedForm.sections?.length || 0} Sections
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>

              {!selectedForm.is_active && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PublishIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => handlePublishClick(e, selectedForm.id)}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    px: 1.75,
                    height: 30,
                    bgcolor: "#2469F0",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
                    },
                  }}
                >
                  Publish
                </Button>
              )}
            </Box>

            <Box sx={{ p: 3, bgcolor: "#F8FAFC" }}>
              <Box
                sx={{
                  mb: 3,
                  p: 1.75,
                  borderRadius: "10px",
                  bgcolor: selectedForm.is_active ? "#F0FDF4" : "#FFFBEB",
                  border: "1px solid",
                  borderColor: selectedForm.is_active ? "#A6F4C5" : "#FEF3C7",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: selectedForm.is_active ? "#027A48" : "#B45309",
                  }}
                >
                  {selectedForm.is_active
                    ? "✓ Active Template"
                    : "📝 Draft Template"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: selectedForm.is_active ? "#027A48" : "#B45309",
                    mt: 0.25,
                  }}
                >
                  {selectedForm.is_active
                    ? "This template is currently live and available for clinical staff."
                    : "This draft template is private. Click publish to make it active."}
                </Typography>
              </Box>

              <FormEdit formId={selectedForm.id} token={token} />
            </Box>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #EAECF0",
              borderRadius: "12px",
              bgcolor: "#FFFFFF",
              height: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CategoryIcon
                sx={{ fontSize: 48, color: "#D0D5DD", mb: 2 }}
              />
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#101828",
                }}
              >
                Select a care plan
              </Typography>
              <Typography
                sx={{ fontSize: "0.75rem", color: "#667085", mt: 0.5 }}
              >
                Choose a template from the left or create a new one
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>

      <ConfirmModal
        show={confirmModal.show}
        handleClose={() => setConfirmModal({ show: false, formId: null })}
        handleConfirm={handleConfirmPublish}
        title="Publish Clinical Template"QK
        body="By publishing this care plan, it will become available for all therapists to assign to their clients. Do you wish to proceed?"
        confirmText="Confirm & Publish"
      />
    </Box>
  );
};

export default CarePlans;